import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNetworkState, useToggle } from 'react-use';
import {
  ConnectionChangePayload,
  ConnectionState,
  CRCProtocol,
  CRCReturnCode,
  Participant,
  ParticipantPropertiesPayload
} from '@zoom/videosdk';

import {
  useLazyGetAppointmentQuery,
  useUpdateCallErrorsMutation
} from 'services/appointments/appointments';
import { UpdateCallErrorsReqProps } from 'services/appointments/appointments.types';
import {
  useLazyGetProviderQuery,
  useLazyGetProviderReviewsQuery
} from 'services/providers/providers';
import { GetReviewResProps } from 'services/providers/providers.types';

import { selectUser } from 'store';

import { notifyError } from 'shared/Toast/Toast';

import { useAppSelector, useZoomCall } from 'hooks';
import { useRouteMatch } from 'hooks/useRouteMatch';
import { PathName } from 'router/paths';

export const useRoomHook = () => {
  const refRoom = useRef<HTMLDivElement>(null);

  const {
    appointment,
    callOutParticipant,
    languageRequested,
    loadingRoom,
    loggerClient,
    resetValuesToDefault,
    room,
    setAppointment,
    setCallOutParticipant,
    setShowControls
  } = useZoomCall();

  const [activeSpeakers, setActiveSpeakers] = useState<Participant[]>([]);
  const [activeParticipant, setActiveParticipant] = useState<Participant>();
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [providerForReview, setProviderForReview] = useState({
    displayName: '',
    profileImage: '',
    userId: ''
  });
  const [isOpenPlatformReview, toggleOpenPlatformReview] = useToggle(false);
  const [isOpenProviderReview, toggleOpenProviderReview] = useToggle(false);
  const [isOpenFeedbackReview, toggleOpenFeedbackReview] = useToggle(false);
  const [participantDisconnected, toggleParticipantDisconnected] = useToggle(false);
  const [connectionLost, toggleConnectionLost] = useToggle(false);
  const { online } = useNetworkState();
  const [callOutState, setCallOutState] = useState<{
    code: CRCReturnCode;
    uuid: string;
  }>();

  useEffect(() => {
    if (!online) {
      const timerFunc = setTimeout(toggleConnectionLost, 20000);
      return () => {
        clearTimeout(timerFunc);
        toggleConnectionLost(false);
      };
    }
  }, [online]);

  const { reviewedProviders } = useAppSelector(selectUser);
  const navigate = useNavigate();
  const isZoomCall = useRouteMatch(PathName.ZoomCall)?.isExact;
  const [getAppointment] = useLazyGetAppointmentQuery();
  const [getReview, { data: reviewData }] = useLazyGetProviderReviewsQuery();
  const [getProvider, { data: providerData }] = useLazyGetProviderQuery();
  const [updateCallErrors] = useUpdateCallErrorsMutation();

  const providerId = appointment?.doctorId;
  const reviewsTotalCount = (reviewData?.info as GetReviewResProps['info'])?.totalCount ?? 0;

  const navigateToHome = () => navigate(PathName.Home);

  const handleLogout = async () => {
    const isCompletedAppointment = appointment?.appointmentStatus === 'completed';
    const providerRating = providerData?.data?.rating ?? 0;
    const isHighRatedProvider = providerRating > 4.5 && reviewsTotalCount > 100;

    const needsProviderReview =
      providerId && (!reviewedProviders?.length || !reviewedProviders.includes(providerId));

    if (isCompletedAppointment) {
      if (isHighRatedProvider) {
        toggleOpenPlatformReview(true);
      } else if (needsProviderReview) {
        toggleOpenProviderReview(true);
      } else {
        if (isZoomCall) leaveFromTheCall();
      }
    } else {
      if (isZoomCall) leaveFromTheCall();
    }

    loggerClient?.reportToGlobalTracing();
    resetValuesToDefault();
  };

  const handleUpdatedAppointment = () => {
    providerId &&
      !providerData?.data &&
      getProvider({
        providerId,
        shouldUpdateState: false
      });

    providerId && !reviewsTotalCount && getReview({ limit: 1, providerId });

    if (appointment) {
      setProviderForReview({
        displayName: appointment.staffName,
        profileImage: appointment.staffImage || '',
        userId: appointment.doctorId || ''
      });
    }

    if (appointment?.appointmentStatus === 'completed' && participantDisconnected) {
      handleLogout();
    }
  };

  const handleSubmitPlatformReview = () => {
    toggleOpenPlatformReview();
    toggleOpenFeedbackReview();
  };

  const handleSubmitProviderReview = () => {
    toggleOpenProviderReview();
    toggleOpenFeedbackReview();
  };

  const handleSkipProviderReview = () => {
    toggleOpenProviderReview();
    navigateToHome();
  };

  const handleCloseFeedbackReview = () => {
    toggleOpenFeedbackReview();
    navigateToHome();
  };

  const handleSkipPlatformReview = () => {
    toggleOpenPlatformReview();
    navigateToHome();
  };

  const handleUpdatedParticipantDisconnected = () => {
    if (!participantDisconnected || !appointment?._id) return;
    getAppointment(appointment._id)
      .unwrap()
      .then(({ data }) => setAppointment(data));
  };

  const handleRoomDisconnected = (event: UpdateCallErrorsReqProps['event'], message?: string) => {
    if (!appointment?._id) return;
    updateCallErrors({
      appointmentId: appointment._id,
      event,
      message
    });
  };

  const handleConnectionChange = (payload: ConnectionChangePayload) => {
    payload.reason && notifyError(payload.reason);

    switch (payload.state) {
      case ConnectionState.Closed:
        handleRoomDisconnected('disconnected', payload.reason);
        handleLogout();
        break;
      case ConnectionState.Reconnecting:
        handleRoomDisconnected('reconnecting', payload.reason);
        break;
      case ConnectionState.Fail:
        handleLogout();
        break;
    }
  };

  const handleUserRemoved = (payload: Array<ParticipantPropertiesPayload>) => {
    // If the user is removed, we need to update the participants list
    setParticipants((prev) =>
      prev.filter((participant) => !payload.some((p) => p.userId === participant.userId))
    );

    const noRemoteParticipant = payload.find((el) => el.userIdentity);
    noRemoteParticipant && toggleParticipantDisconnected(true);
  };

  const leaveFromTheCall = () => {
    toggleParticipantDisconnected(false);
    navigateToHome();
    resetValuesToDefault();
  };

  const handleUserAdded = (data: Participant[]) => {
    const isUserAlreadyConnected = data.find(
      (el) => el.userIdentity === room?.getCurrentUserInfo()?.userIdentity
    );
    if (isUserAlreadyConnected) {
      leaveFromTheCall();
      return;
    }
    toggleParticipantDisconnected(false);
    setParticipants((prev: Participant[]) => {
      const prevMap = new Map(prev.map((p) => [p.userId, p]));
      for (const p of data) {
        prevMap.set(p.userId, p);
      }
      return Array.from(prevMap.values());
    });
  };

  const handleUserUpdated = (payload: ParticipantPropertiesPayload[]) => {
    setParticipants((prev) => {
      const updatedParticipants = prev.map((participant) => {
        const updated = payload.find((p) => p.userId === participant.userId);
        return updated ? { ...participant, ...updated } : participant;
      });
      return updatedParticipants;
    });
  };

  const handleActiveSpeaker = (payload: Participant[]) => {
    setActiveSpeakers(payload);
    setTimeout(() => setActiveSpeakers([]), 1000);
  };

  const handleUpdatedParticipants = () => {
    const callOutParticipant = participants.find((el) => !el.userIdentity);

    setCallOutParticipant(callOutParticipant || null);

    setActiveParticipant(
      callOutParticipant ||
        participants.find((el) => el.userId !== room?.getCurrentUserInfo()?.userId)
    );
  };

  const callCRCDevice = () => {
    if (!room || !languageRequested) return;
    if (room.getAllUser().find((el) => !el.userIdentity)) return;
    room
      .getMediaStream()
      .callCRCDevice(languageRequested.value, CRCProtocol.SIP)
      .catch(({ reason }) => notifyError(reason));
  };

  useEffect(callCRCDevice, [languageRequested, room, callOutParticipant]);

  useEffect(handleUpdatedAppointment, [appointment]);

  useEffect(handleUpdatedParticipantDisconnected, [participantDisconnected, appointment]);

  useEffect(handleUpdatedParticipants, [participants, callOutState]);

  useEffect(() => {
    setParticipants(room?.getAllUser() ?? []);

    room?.on('user-added', handleUserAdded);
    room?.on('user-updated', handleUserUpdated);
    room?.on('user-removed', handleUserRemoved);
    room?.on('active-speaker', handleActiveSpeaker);
    room?.on('connection-change', handleConnectionChange);
    room?.on('crc-call-out-state-change', setCallOutState);

    return () => {
      room?.off('user-added', handleUserAdded);
      room?.off('user-updated', handleUserUpdated);
      room?.off('user-removed', handleUserRemoved);
      room?.off('active-speaker', handleActiveSpeaker);
      room?.off('connection-change', handleConnectionChange);
      room?.off('crc-call-out-state-change', setCallOutState);
    };
  }, [room]);

  useEffect(() => {
    !isZoomCall && loadingRoom && handleLogout();
  }, [isZoomCall, loadingRoom]);

  useEffect(() => {
    if (!room) return;
    let timeout: ReturnType<typeof setTimeout>;

    const handleSetShowControls = () => {
      setShowControls(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => setShowControls(false), 5000);
    };

    handleSetShowControls();

    const node = refRoom.current;
    node?.addEventListener('mousemove', handleSetShowControls);
    node?.addEventListener('touchstart', handleSetShowControls);

    return () => {
      node?.removeEventListener('mousemove', handleSetShowControls);
      node?.removeEventListener('touchstart', handleSetShowControls);
      clearTimeout(timeout);
    };
  }, [room]);

  return {
    activeParticipant,
    activeSpeakers,
    connectionLost,
    handleCloseFeedbackReview,
    handleLogout,
    handleSkipPlatformReview,
    handleSkipProviderReview,
    handleSubmitPlatformReview,
    handleSubmitProviderReview,
    interpreterRequested: callOutState?.code === CRCReturnCode.Ringing,
    isOpenFeedbackReview,
    isOpenPlatformReview,
    isOpenProviderReview,
    participantDisconnected,
    participants,
    providerForReview,
    refRoom
  };
};
